import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { router } from '@inertiajs/react';
import { CheckCircle, ChevronDown, Check, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface BackendFilters {
  q: string;
  city: string;
  region: string;
  neighborhood_id: string;
  verified: boolean;
  new_profile: boolean;
  available_now: boolean;
  services: string[];
  price_min: number;
  price_max: number;
  attrs: Record<string, string | string[]>;
  sort: string;
}

interface QuickFilterStripProps {
  filters: BackendFilters;
}

function currentGenderPath(): string {
  if (typeof window === 'undefined') return '/';
  const path = window.location.pathname;
  if (path === '/male' || path === '/trans') return path;
  return '/';
}

function filtersToParams(filters: BackendFilters): Record<string, unknown> {
  const params: Record<string, unknown> = {};
  if (filters.q) params.q = filters.q;
  if (filters.city) params.city = filters.city;
  if (filters.region) params.region = filters.region;
  if (filters.neighborhood_id) params.neighborhood_id = filters.neighborhood_id;
  if (filters.verified) params.verified = '1';
  if (filters.new_profile) params.new = '1';
  if (filters.available_now) params.available_now = '1';
  if (filters.services?.length) params.services = filters.services;
  if (filters.price_min > 0) params.price_min = filters.price_min;
  if (filters.price_max < 1000) params.price_max = filters.price_max;
  if (filters.attrs && Object.keys(filters.attrs).length) params.attrs = filters.attrs;
  if (filters.sort && filters.sort !== 'top') params.sort = filters.sort;
  return params;
}

function visit(nextFilters: Partial<BackendFilters>, base: BackendFilters) {
  const merged = { ...base, ...nextFilters };
  router.visit(currentGenderPath(), {
    data: filtersToParams(merged) as Record<string, string | number | boolean | undefined>,
    preserveState: false,
    preserveScroll: false,
  });
}

export function QuickFilterStrip({ filters }: QuickFilterStripProps) {
  const { t } = useTranslation('common');

  const sortOptions = [
    { value: 'top', label: t('discovery.sort_top') },
    { value: 'online', label: t('discovery.sort_online') },
    { value: 'rated', label: t('discovery.sort_rated') },
    { value: 'newest', label: t('discovery.sort_newest') },
  ];

  const activeSort = filters.sort || 'top';
  const activeSortLabel =
    sortOptions.find((o) => o.value === activeSort)?.label ?? t('discovery.sort_top');

  return (
    <div className="border-b border-border bg-background">
      <div className="container mx-auto px-4">
        <div className="flex items-center gap-2 overflow-x-auto py-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {/* Online now chip */}
          <Button
            onClick={() => visit({ available_now: !filters.available_now }, filters)}
            variant={filters.available_now ? 'secondary' : 'outline'}
            size="pill"
            className="shrink-0"
          >
            <span className="mr-1.5 inline-block h-2 w-2 rounded-full bg-green-500" />
            {t('discovery.online_now')}
          </Button>

          {/* Verified chip */}
          <Button
            onClick={() => visit({ verified: !filters.verified }, filters)}
            variant={filters.verified ? 'secondary' : 'outline'}
            size="pill"
            className="shrink-0"
          >
            <CheckCircle className="mr-1.5 h-3.5 w-3.5" />
            {t('discovery.verified')}
          </Button>

          {/* New chip */}
          <Button
            onClick={() => visit({ new_profile: !filters.new_profile }, filters)}
            variant={filters.new_profile ? 'secondary' : 'outline'}
            size="pill"
            className="shrink-0"
          >
            <Sparkles className="mr-1.5 h-3.5 w-3.5" />
            {t('discovery.new_profiles')}
          </Button>

          {/* Sort dropdown — pinned to the right */}
          <div className="ml-auto shrink-0">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="pill"
                  className="gap-1.5"
                >
                  {t('discovery.sort')}: {activeSortLabel}
                  <ChevronDown className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-36">
                {sortOptions.map((option) => (
                  <DropdownMenuItem
                    key={option.value}
                    onClick={() => visit({ sort: option.value }, filters)}
                    className="flex items-center justify-between gap-3"
                  >
                    {option.label}
                    {activeSort === option.value && (
                      <Check className="h-3.5 w-3.5 text-primary" />
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </div>
  );
}
